// src/components/WorldMap.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { indiaHeatData, cityNames } from '../data/heatData';
import StateBoundaries from './StateBoundaries';
import HeatMapLayer from './HeatMap';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom markers for different intensities
const createCustomIcon = (
  intensity: number,
  markerLabel: string,
  showNumber: boolean = true,
) => {
  let color = '#235AF9';

  if (intensity > 0.8) color = '#FF0000';
  else if (intensity > 0.6) color = '#FF9800';
  else if (intensity > 0.4) color = '#FF9800';
  else if (intensity > 0.2) color = '#F2C94C';

  return L.divIcon({
    html: `
      <div style="
        width: ${showNumber ? '28px' : '20px'};
        height: ${showNumber ? '28px' : '20px'};
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${showNumber ? '10px' : '0'};
        cursor: pointer;
        transition: all 0.3s ease;
      ">
        ${showNumber ? markerLabel : ''}
      </div>
    `,
    className: 'custom-marker',
    iconSize: showNumber ? [28, 28] : [20, 20],
    iconAnchor: showNumber ? [14, 14] : [10, 10],
  });
};

const formatCompactCount = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return value.toString();
};

// Component to handle zoom changes
function ZoomHandler({
  onZoomChange }: { onZoomChange: (zoom: number) => void }) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });
  return null;
}

// Custom Zoom Controls Component
function CustomZoomControls({ mapRef }: { mapRef: React.MutableRefObject<L.Map | null> }) {
  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '30px',
        right: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 1000,
      }}
    >
      <button
        onClick={handleZoomIn}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          border: '2px solid #0066cc',
          backgroundColor: 'white',
          cursor: 'pointer',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          color: '#01579b',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E0ECFD';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(0,102,204,0.3)';
        }}
        onMouseOut={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'white';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }}
      >
        +
      </button>
      <button
        onClick={handleZoomOut}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          border: '2px solid #0066cc',
          backgroundColor: 'white',
          cursor: 'pointer',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          color: '#01579b',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E0ECFD';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(0,102,204,0.3)';
        }}
        onMouseOut={(e) => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'white';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }}
      >
        −
      </button>
    </div>
  );
}

interface HeatMapDataPoint {
  warning_type: string;
  city: string;
  state: string | null;
  lat: string;
  long: string;
  count: number;
}

const WorldMap: React.FC<{
  selectedAlertType?: string | null;
  heatMapData?: HeatMapDataPoint[];
  userCountry?: 'India' | 'Nigeria' | 'Zambia' | 'Saudi Arabia';
}> = ({ selectedAlertType = null, heatMapData = [], userCountry = 'India' }) => {
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [showStateBoundaries, setShowStateBoundaries] = useState(true);
  const [showCityMarkers, setShowCityMarkers] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [markerShowNumbers, setMarkerShowNumbers] = useState(false);
  const mapRef = React.useRef<L.Map | null>(null);

  // Country center coordinates and zoom levels
  const countrySettings: {
    [key: string]: { center: [number, number]; zoom: number; bounds: L.LatLngBounds };
  } = {
    India: {
      center: [20.5937, 78.9629],
      zoom: 5,
      bounds: L.latLngBounds([8, 68], [35, 97]),
    },
    Nigeria: {
      center: [9.082, 8.6753],
      zoom: 6,
      bounds: L.latLngBounds([4, 3], [14, 15]),
    },
    Zambia: {
      center: [-13.1339, 27.8493],
      zoom: 6,
      bounds: L.latLngBounds([-18, 22], [-8, 34]),
    },
    'Saudi Arabia': {
      center: [24.7136, 46.6753],
      zoom: 6,
      bounds: L.latLngBounds([16, 34], [32, 56]),
    },
  };

  // Auto-zoom to user's country on mount
  useEffect(() => {
    if (mapRef.current && userCountry) {
      const settings = countrySettings[userCountry];
      if (settings) {
        mapRef.current.setView(settings.center, settings.zoom);
        // Optionally, fit bounds for better view
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.fitBounds(settings.bounds, { padding: [50, 50] });
          }
        }, 100);
      }
    }
  }, [userCountry]);

  // Calculate bounds from heatmap data
  const calculateBounds = (data: Array<[number, number, number, number, string]>) => {
    if (data.length === 0) {
      // Default to user's country bounds
      return countrySettings[userCountry]?.bounds || L.latLngBounds([8, 68], [35, 97]);
    }

    let minLat = data[0][0];
    let maxLat = data[0][0];
    let minLng = data[0][1];
    let maxLng = data[0][1];

    data.forEach((point) => {
      minLat = Math.min(minLat, point[0]);
      maxLat = Math.max(maxLat, point[0]);
      minLng = Math.min(minLng, point[1]);
      maxLng = Math.max(maxLng, point[1]);
    });

    // Add padding to bounds (10% on each side)
    const latPadding = (maxLat - minLat) * 0.1;
    const lngPadding = (maxLng - minLng) * 0.1;

    return L.latLngBounds(
      [minLat - latPadding, minLng - lngPadding],
      [maxLat + latPadding, maxLng + lngPadding],
    );
  };

  // Transform API data to heatmap format and filter based on selected alert type
  const filteredHeatData = useMemo(() => {
    // If API data is provided and has content, use it
    if (heatMapData && Array.isArray(heatMapData) && heatMapData.length > 0) {

      // Filter by selected alert type and exclude null states
      let filteredData = heatMapData;
      if (selectedAlertType && selectedAlertType !== '') {
        filteredData = heatMapData.filter(
          (d) => d.warning_type === selectedAlertType && d.state !== null && d.state?.trim(),
        );
      } else {
        // Filter out null states when showing all data
        filteredData = heatMapData.filter((d) => d.state !== null && d.state?.trim());
      }

      // If no data after filtering, return empty array
      if (filteredData.length === 0) {
        return [];
      }

      // Group by location (lat, long) and sum counts, track city names and states
      const locationMap = new Map<string, number>();
      const stateMap = new Map<string, string[]>(); // Track states for each location
      const cityNameMap = new Map<string, string>(); // Track city names for each location

      filteredData.forEach((item) => {
        // Convert lat and long to numbers (they might be strings from API)
        const lat = parseFloat(item.lat);
        const lng = parseFloat(item.long);

        // Skip invalid coordinates
        if (isNaN(lat) || isNaN(lng)) {
          return;
        }

        const key = `${lat},${lng}`;
        const currentCount = locationMap.get(key) || 0;
        locationMap.set(key, currentCount + item.count);

        // Track city name (use the first city name found for this location)
        if (!cityNameMap.has(key)) {
          cityNameMap.set(key, item.city);
        }

        // Track states
        const states = stateMap.get(key) || [];
        if (item.state && !states.includes(item.state)) {
          states.push(item.state);
        }
        stateMap.set(key, states);
      });

      // Get max count from aggregated data for intensity scaling
      const maxCount = Math.max(...Array.from(locationMap.values()), 1);

      // Convert to heatmap format: [lat, lng, intensity (0-1), count, cityName]
      // Intensity based on actual count value (higher count = more intense/red)
      const heatmapPoints: Array<[number, number, number, number, string]> = [];
      locationMap.forEach((count, key) => {
        const [lat, lng] = key.split(',').map(Number);
        const cityName = cityNameMap.get(key) || 'Unknown City';
        // Calculate intensity based on count: higher count = higher intensity (closer to 1)
        const intensity = Math.min(count / maxCount, 1);
        heatmapPoints.push([lat, lng, intensity, count, cityName]);
      });

      // Log states where this alert type is present
      const affectedStates = new Set<string>();
      stateMap.forEach((states) => {
        states.forEach((state) => affectedStates.add(state));
      });

      // Only return if we have valid points, otherwise return empty array to show blank map
      return heatmapPoints.length > 0 ? heatmapPoints : [];
    }
    
    // If no API data, return empty array (blank map)
    return [];
  }, [selectedAlertType, heatMapData]);

  // Handle zoom level changes
  useEffect(() => {
    if (currentZoom > 7) {
      // When zoomed in - show markers, hide state boundaries
      setShowStateBoundaries(false);
      setShowCityMarkers(true);

      // Show numbers in markers only after full transition
      setTimeout(() => {
        setMarkerShowNumbers(true);
      }, 300);
    } else {
      // When zoomed out - show state boundaries, hide markers
      setShowStateBoundaries(true);
      setShowCityMarkers(false);
      setMarkerShowNumbers(false);
    }
  }, [currentZoom]);

  // Auto-focus map on data load
  useEffect(() => {
    if (mapRef.current && filteredHeatData.length > 0) {
      const bounds = calculateBounds(filteredHeatData);
      // Use fitBounds with maxZoom option to control zoom level
      mapRef.current.fitBounds(bounds, { maxZoom: 6, padding: [50, 50] });
    }
  }, [filteredHeatData]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      {/* Zoom Level Indicator */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '10px 15px',
          borderRadius: '8px',
          zIndex: 1000,
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          fontFamily: 'Arial, sans-serif',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: showStateBoundaries ? '#4caf50' : '#FF9800',
            animation: showStateBoundaries ? 'pulse 2s infinite' : 'none',
          }}
        ></div>
        <span style={{ color: '#333333' }}>{showStateBoundaries ? '🗺️ State View' : '📍 City View'}</span>
        {/* <span
          style={{
            color: '#666666',
            fontSize: '12px',
            marginLeft: '8px',
            padding: '2px 8px',
            backgroundColor: '#F2F2F2',
            borderRadius: '4px',
          }}
        >
          Zoom: {currentZoom}
        </span> */}
      </div>

      <MapContainer
        ref={mapRef}
        center={[22.0, 78.0]}
        zoom={4}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        attributionControl={false}
        minZoom={4}
        maxZoom={12}
        preferCanvas={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxNativeZoom={18}
          maxZoom={12}
          minZoom={4}
          tileSize={256}
          crossOrigin="anonymous"
          opacity={1}
          zIndex={0}
        />

        {/* Heatmap Layer - Always visible but with different opacity */}
        <HeatMapLayer
          data={filteredHeatData.map((point) => [point[0], point[1], point[2]] as const)}
          radius={showStateBoundaries ? 18 : 15}
          blur={showStateBoundaries ? 10 : 6}
          maxZoom={15}
          minOpacity={showStateBoundaries ? 0.4 : 0.6}
        />

        {/* State Boundaries Layer */}
        <StateBoundaries
          visible={showStateBoundaries}
          heatMapData={heatMapData}
          selectedAlertType={selectedAlertType}
          country={userCountry}
        />

        {/* City Markers Layer */}
        {showCityMarkers &&
          filteredHeatData.map((point, index) => {
            const [lat, lng, intensity, count, cityName] = point;

            return (
              <Marker
                key={`city-${index}`}
                position={[lat, lng]}
                icon={createCustomIcon(intensity, formatCompactCount(count), markerShowNumbers)}
                eventHandlers={{
                  click: () => {
                    setSelectedCity(cityName);
                  },
                  mouseover: (e) => {
                    e.target.openPopup();
                  },
                  mouseout: (e) => {
                    e.target.closePopup();
                  },
                }}
              >
                <Popup>
                  <div style={{ padding: '10px', minWidth: '180px' }}>
                    <strong style={{ fontSize: '16px', color: '#333333' }}>{cityName}</strong>
                    <div style={{ marginTop: '8px', fontSize: '14px' }}>
                      <div>📍 Lat: {lat.toFixed(4)}</div>
                      <div>📍 Lng: {lng.toFixed(4)}</div>
                      <div style={{ marginTop: '8px' }}>
                        <div>
                          📊 Count: <strong>{count.toLocaleString()}</strong>
                        </div>
                        <div
                          style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#E0E0E0',
                            borderRadius: '4px',
                            marginTop: '5px',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${intensity * 100}%`,
                              height: '100%',
                              backgroundColor:
                                intensity > 0.7 ? '#FF0000' : intensity > 0.4 ? '#FF9800' : '#235AF9',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}

        <ZoomHandler onZoomChange={setCurrentZoom} />
      </MapContainer>

      {/* Custom Zoom Controls */}
      <CustomZoomControls mapRef={mapRef} />

      {/* Selected City Info Panel */}
      {selectedCity && (
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '15px',
            borderRadius: '10px',
            zIndex: 1000,
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            maxWidth: '300px',
            fontFamily: 'Arial, sans-serif',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <h3 style={{ margin: 0, color: '#333333' }}>📍 {selectedCity}</h3>
            <button
              onClick={() => setSelectedCity(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#666666',
              }}
            >
              ✕
            </button>
          </div>
          <p style={{ margin: 0, color: '#666666', fontSize: '14px' }}>
            Click on any city marker to see details here
          </p>
        </div>
      )}

      {/* Add CSS animation for pulsing effect */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
          
          .custom-marker:hover {
            transform: scale(1.2);
            transition: transform 0.2s ease;
          }

          .leaflet-container {
            background: #f0f0f0;
          }

          .leaflet-tile-pane {
            z-index: 2 !important;
          }

          .leaflet-overlay-pane {
            z-index: 3 !important;
          }
        `}
      </style>
    </div>
  );
};

export default WorldMap;
